/**
 * Logros por acción ("trofeos") de Galia
 * --------------------------------------
 * Galia identifica acciones repetidas del estudiante (saludar, agradecer,
 * preguntar…) y, al alcanzar un umbral, desbloquea un trofeo. Idea de Santiago
 * (reunión 02/06/2026): "saludó 3 veces → trofeo de los saludos", y la
 * recompensa puede revelar una pieza del paisaje ("el arbolito").
 *
 * La detección es del lado cliente sobre el texto del estudiante (incluye la
 * transcripción de voz). El texto se normaliza (minúsculas, sin tildes,
 * apóstrofes rectos) antes de buscar los `match`.
 *
 * ALCANCE: catálogo curable y contadores persistentes. Umbrales y textos son
 * fáciles de ajustar. Cada trofeo revela una pieza del paisaje — el mapeo
 * trofeo → pieza vive en SCENE_PIECES (galiaScenes.ts, campo `achievement`).
 */

export interface ActionAchievement {
  /** Id estable (clave de persistencia). */
  id: string;
  /** Emoji del trofeo. */
  emoji: string;
  /** Nombre del trofeo en español (apoyo). */
  title: string;
  /**
   * Nombre del trofeo en FRANCÉS — es el que se muestra grande: el logro
   * celebra un acto en francés, así el trofeo también enseña.
   */
  titleFr: string;
  /** Frase corta al desbloquear (español, explica). */
  blurb: string;
  /** Pista de cómo lograrlo (español) — se muestra en trofeos bloqueados. */
  hint: string;
  /** Veces que hay que hacer la acción para desbloquear. */
  threshold: number;
  /**
   * Subcadenas (ya normalizadas: minúsculas/sin tildes/apóstrofe recto) que,
   * si aparecen en el mensaje, cuentan como que hizo la acción. Basta una.
   */
  match: string[];
  /**
   * Detección especial además de `match` (p. ej. preguntas por el signo "?").
   */
  special?: 'question';
}

/**
 * Primer catálogo. Saludar es el ejemplo canónico de Santiago y revela el árbol.
 */
export const ACTION_ACHIEVEMENTS: ActionAchievement[] = [
  {
    id: 'greeting',
    emoji: '👋',
    title: 'Trofeo de los saludos',
    titleFr: 'Trophée des salutations',
    blurb: 'Saludaste a Galia en francés varias veces. ¡Bonjour!',
    hint: 'Saluda a Galia en francés (bonjour, salut…)',
    threshold: 3,
    match: ['bonjour', 'salut', 'bonsoir', 'coucou'],
  },
  {
    id: 'farewell',
    emoji: '🌙',
    title: 'Trofeo de las despedidas',
    titleFr: 'Trophée des au revoir',
    blurb: 'Te despediste con estilo. À bientôt !',
    hint: 'Despídete en francés (au revoir, à bientôt…)',
    threshold: 3,
    match: ['au revoir', 'a bientot', 'a plus', 'bonne nuit', 'bonne journee', 'adieu'],
  },
  {
    id: 'thanks',
    emoji: '🙏',
    title: 'Trofeo de la gratitud',
    titleFr: 'Trophée de la gratitude',
    blurb: 'Diste las gracias como un local. Merci !',
    hint: 'Da las gracias en francés (merci…)',
    threshold: 3,
    match: ['merci', 'je te remercie', 'je vous remercie'],
  },
  {
    id: 'introduce',
    emoji: '🪪',
    title: 'Trofeo de la presentación',
    titleFr: 'Trophée de la présentation',
    blurb: 'Te presentaste en francés. Enchanté !',
    hint: 'Preséntate en francés (je m’appelle…)',
    threshold: 2,
    match: ["je m'appelle", 'je suis ', "moi c'est", "j'ai "],
  },
  {
    id: 'question',
    emoji: '❓',
    title: 'Trofeo de la curiosidad',
    titleFr: 'Trophée de la curiosité',
    blurb: 'Hiciste muchas preguntas. ¡Así se aprende!',
    hint: 'Hazle preguntas a Galia en francés',
    threshold: 5,
    match: ['est-ce que', "qu'est-ce", 'pourquoi', 'comment', 'combien', 'quand', ' ou ', 'quel', 'quelle'],
    special: 'question',
  },
  {
    id: 'politeness',
    emoji: '🎩',
    title: 'Trofeo de la cortesía',
    titleFr: 'Trophée de la politesse',
    blurb: 'Fuiste muy cortés. S’il vous plaît !',
    hint: 'Usa fórmulas de cortesía (s’il vous plaît, pardon…)',
    threshold: 3,
    match: ["s'il te plait", "s'il vous plait", 'pardon', 'excusez-moi', 'excuse-moi', 'de rien'],
  },
  {
    id: 'opinion',
    emoji: '💬',
    title: 'Trofeo de la opinión',
    titleFr: 'Trophée de l’opinion',
    blurb: 'Diste tu opinión en francés. À mon avis…',
    hint: 'Da tu opinión en francés (je pense que…)',
    threshold: 3,
    match: ['je pense que', 'je crois que', 'a mon avis', 'je trouve que', 'selon moi'],
  },
];

/** Mapa id → logro para acceso rápido. */
export const ACHIEVEMENTS_BY_ID: Record<string, ActionAchievement> = Object.fromEntries(
  ACTION_ACHIEVEMENTS.map((a) => [a.id, a])
);

/**
 * Normaliza un texto para la detección: minúsculas, sin tildes/diacríticos,
 * apóstrofes curvos → rectos, espacios colapsados con márgenes.
 */
export function normalizeForMatch(text: string): string {
  const stripped = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // quitar diacríticos combinados
    .replace(/[‘’ʼ`]/g, "'"); // apóstrofes curvos → recto
  // Envolver en espacios para que match con espacios (" ou ", "je suis ") funcione en bordes.
  return ` ${stripped.replace(/\s+/g, ' ').trim()} `;
}

/**
 * Devuelve los ids de acciones que este mensaje del estudiante satisface.
 * Cuenta como máximo una vez por acción por mensaje.
 */
export function detectActions(rawText: string): string[] {
  if (!rawText || !rawText.trim()) return [];
  const norm = normalizeForMatch(rawText);
  const hit: string[] = [];
  for (const a of ACTION_ACHIEVEMENTS) {
    let matched = a.match.some((m) => norm.includes(m));
    if (!matched && a.special === 'question' && rawText.includes('?')) {
      matched = true;
    }
    if (matched) hit.push(a.id);
  }
  return hit;
}
